import { api } from './api';

interface ApiResponse<T> {
  success: boolean;
  code: number;
  message: string;
  data: T;
}

/** Connection settings for one provider. Provider-specific keys are allowed. */
export interface ProviderConfig {
  // SMTP
  host?: string | null;
  port?: number | null;
  username?: string | null;
  /** Write-only. Supplied as plaintext, encrypted on save, never returned —
   *  reads come back masked, so a blank field means "keep the stored one". */
  password?: string | null;
  fromEmail?: string | null;
  fromName?: string | null;
  useTls?: boolean | null;
  // Non-SMTP / other channels
  apiKey?: string | null;
  token?: string | null;
  baseUrl?: string | null;
  instanceId?: string | null;
  [key: string]: unknown;
}

export interface IntegrationConfig {
  _id: string;
  channel: string;
  provider: string;
  enabled: boolean;
  isDefault: boolean;
  isActive: boolean;
  config: ProviderConfig;
  capabilities: string[];
  defaults?: Record<string, unknown>;
  limits?: Record<string, unknown>;
  webhook?: Record<string, unknown>;
}

export const integrationConfigService = {
  list: async (channel?: string): Promise<IntegrationConfig[]> => {
    const response = await api.get<ApiResponse<IntegrationConfig[]>>(
      '/api/v1/integration-config/list', { params: { channel } });
    // The list endpoint answers with the standard envelope; some deployments
    // return a bare array, so both shapes are accepted rather than crashing the
    // screen on a shape difference.
    const data = response.data as unknown as { data?: IntegrationConfig[] };
    return (data?.data ?? (response.data as unknown as IntegrationConfig[])) || [];
  },

  update: async (configId: string, payload: Partial<IntegrationConfig>): Promise<IntegrationConfig> => {
    const response = await api.put<ApiResponse<IntegrationConfig>>(
      `/api/v1/integration-config/update/${configId}`, payload);
    return response.data.data;
  },

  create: async (payload: Partial<IntegrationConfig>): Promise<IntegrationConfig> => {
    const response = await api.post<ApiResponse<IntegrationConfig>>(
      '/api/v1/integration-config/create', payload);
    return response.data.data;
  },
};

// ---------------------------------------------------------------------------
// Provider field descriptors
// ---------------------------------------------------------------------------
// `config` is schemaless by design — every provider needs different connection
// fields — so the shape is DESCRIBED by the backend rather than hardcoded here.
// Keeping the list in one place is the point: a form built from a local copy
// drifts from the adapter that reads the values, and the symptom is a control
// that saves and does nothing.

export type ProviderFieldType = 'string' | 'number' | 'boolean' | 'password' | 'select';

export interface ProviderFieldOption {
  label: string;
  value: string;
}

export interface ProviderField {
  name: string;
  label: string;
  type: ProviderFieldType;
  /** Which block of the document the value belongs to — `config` or `webhook`. */
  block: string;
  required: boolean;
  /** Encrypted on save and never returned. Rendered write-only. */
  secret: boolean;
  /** Has a working default; only touched when a provider changes its API. */
  advanced: boolean;
  hint?: string | null;
  placeholder?: string | null;
  options?: ProviderFieldOption[] | null;
  default?: unknown;
}

/** A shortcut that fills several fields at once, e.g. a known SMTP host. */
export interface ProviderPreset {
  key: string;
  label: string;
  values: Record<string, unknown>;
  hint?: string | null;
}

export interface ProviderDescriptor {
  channel: string;
  provider: string;
  fields: ProviderField[];
  presets: ProviderPreset[];
}

export const getProviderFields = async (channel?: string): Promise<ProviderDescriptor[]> => {
  const response = await api.get<ApiResponse<ProviderDescriptor[]>>(
    '/api/v1/integration-config/provider-fields', { params: { channel } });
  const data = response.data as unknown as { data?: ProviderDescriptor[] };
  return (data?.data ?? (response.data as unknown as ProviderDescriptor[])) || [];
};

/** The descriptor for one provider, or `undefined` when it has none.
 *  Undescribed is expected, not an error — the caller edits the raw object. */
export const descriptorFor = (
  descriptors: ProviderDescriptor[], channel: string, provider: string,
): ProviderDescriptor | undefined =>
  descriptors.find(
    (d) => d.channel === (channel || '').toLowerCase()
      && d.provider === (provider || '').toLowerCase());

/** Which preset a stored configuration matches. A preset matches when every value
 *  it would set is already set — so a host with an edited port is NOT reported as
 *  that preset, and the dropdown does not claim a configuration it does not describe. */
export const matchingPreset = (
  presets: ProviderPreset[], values: Record<string, unknown>,
): string => {
  const found = presets.find((p) => Object.keys(p.values).length > 0
    && Object.entries(p.values).every(([k, v]) => values[k] === v));
  return found ? found.key : 'custom';
};
